import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import api from "@/lib/api";
import AppShell from "@/components/AppShell";
import { Button } from "@/components/ui/button";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import { CreditCard, Receipt, AlertTriangle } from "lucide-react";

export default function Billing() {
  const { refresh, user } = useAuth();
  const nav = useNavigate();
  const [billing, setBilling] = useState(null);
  const [busy, setBusy] = useState(false);

  const load = () => api.get("/payments/billing").then((r) => setBilling(r.data));
  useEffect(() => { load(); }, []);

  const cancel = async () => {
    if (!window.confirm("Cancel your Hope Chest+ membership? Your data stays with you — you'll just return to the Free plan.")) return;
    setBusy(true);
    try {
      await api.post("/payments/cancel");
      toast.success("Membership canceled. We're here whenever you're ready to come back.");
      await refresh();
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Could not cancel");
    } finally { setBusy(false); }
  };

  if (!billing) return <AppShell><div className="p-10 text-[#7A736E]">Loading…</div></AppShell>;

  return (
    <AppShell>
      <div className="max-w-3xl mx-auto px-6 py-12">
        <h1 className="font-display text-3xl md:text-4xl font-semibold">Membership & billing</h1>
        <p className="text-[#7A736E] mt-2">Manage your Hope Chest+ membership.</p>

        <div className="hc-card p-6 mt-8" data-testid="billing-current-plan">
          <div className="flex items-start justify-between gap-4">
            <div className="flex items-start gap-4">
              <div className="w-12 h-12 rounded-2xl bg-[#D8E2D8] flex items-center justify-center">
                <CreditCard className="w-5 h-5 text-[#3A5A4A]" />
              </div>
              <div>
                <p className="text-xs uppercase tracking-wider text-[#7A736E]">Current plan</p>
                <p className="font-display text-2xl mt-1">{billing.tier === "paid" ? "Hope Chest+ Premium" : "Free"}</p>
                {billing.subscription_canceled_at && <p className="text-xs text-[#7A736E] mt-1">Canceled {new Date(billing.subscription_canceled_at).toLocaleDateString()}</p>}
              </div>
            </div>
            {billing.tier === "paid" ? (
              <Button onClick={cancel} disabled={busy} variant="outline" className="rounded-full border-[#C87B67] text-[#C87B67] hover:bg-[#C87B67]/5" data-testid="cancel-membership-button">
                {busy ? "Canceling…" : "Cancel membership"}
              </Button>
            ) : (
              <Button onClick={() => nav("/pricing")} className="rounded-full bg-[#3A5A4A] hover:bg-[#2C4538]" data-testid="upgrade-from-billing">
                Upgrade to Premium
              </Button>
            )}
          </div>
          {billing.tier === "paid" && (
            <div className="mt-4 p-4 bg-[#F3EFEA] rounded-xl flex gap-3 text-sm text-[#7A736E]">
              <AlertTriangle className="w-4 h-4 text-[#C87B67] mt-0.5 flex-shrink-0" />
              <span>Canceling will move you back to the Free plan. Any additional Hope Chests beyond your first will become read-only until you re-upgrade.</span>
            </div>
          )}
        </div>

        <div className="hc-card p-6 mt-6">
          <div className="flex items-center gap-3 mb-4">
            <Receipt className="w-5 h-5 text-[#3A5A4A]" />
            <h2 className="font-display text-lg">Payment history</h2>
          </div>
          {billing.transactions.length === 0 ? (
            <p className="text-sm text-[#7A736E]">No payments yet.</p>
          ) : (
            <div className="space-y-2">
              {billing.transactions.map((t) => (
                <div key={t.session_id} className="flex justify-between items-center py-2 border-b border-[#EBE5DF] last:border-0" data-testid={`txn-${t.session_id}`}>
                  <div>
                    <p className="text-sm font-medium">{t.package_id === "yearly" ? "Premium — Yearly" : "Premium — Monthly"}</p>
                    <p className="text-xs text-[#7A736E]">{new Date(t.created_at).toLocaleDateString()} • {t.payment_status}</p>
                  </div>
                  <p className="text-sm font-medium">${t.amount.toFixed(2)}</p>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </AppShell>
  );
}
